import { useRef, useState } from "react";

import { FilterResponse } from "@/api/generatedTypes";
import { AppliedFiltersCategory } from "@/components/filter/AppliedFiltersCategory";
import { AppliedFiltersItem } from "@/components/filter/AppliedFiltersItem";
import { getDiscountDisplayName } from "@/components/filter/filterHelpers";
import { SecondaryCategorySelect } from "@/components/filter/SecondaryCategorySelect";
import { SecondFilterSelect } from "@/components/filter/secondFilterSelectedState";
import { useFilterHandlers } from "@/components/filter/useFilterHandlers";
import { useClickOutside } from "@/hooks/useClickOutside";
import useSelectedCategory from "@/hooks/useSelectedCategory";

import styles from "./AppliedFilters.module.scss";

const FILTERS = ["colors", "sizes", "discounts"];

const AppliedFilters = () => {
  const [categoryListOpened, setCategoryListOpened] = useState(false);
  const categoryListRef = useRef<HTMLDivElement | null>(null);

  const closeCategoryList = (e?: MouseEvent | TouchEvent) => {
    if (
      e?.target &&
      (e.target as HTMLElement).closest("#applied_category_label")
    ) {
      return;
    }
    setCategoryListOpened(false);
  };

  useClickOutside(categoryListRef, closeCategoryList);

  const {
    appliedFilters: appliedFilterIds,
    selectAndApplySingleSecondFilter,
    filterQuery,
    applyCategories,
    applyCollections,
    appliedSearch,
    resetSearch,
    resetAllExceptStores,
  } = useFilterHandlers();

  const {
    selectedCategoryId,
    selectedRootCategory,
    selectedCategory,
    selectedCollectionId,
    selectedRootCollection,
    selectedCollection,
  } = useSelectedCategory();

  const appliedFilters = FILTERS.reduce(
    (acc: AppliedFilterInterface[], filterKey) => {
      if (!filterQuery.data?.data) return acc;
      if (!appliedFilterIds[filterKey]) return acc;
      acc = [
        ...acc,
        ...appliedFilterIds[filterKey].map((id) =>
          getFilterItem(
            filterQuery.data?.data,
            filterKey as keyof SecondFilterSelect,
            id
          )
        ),
      ];
      return acc;
    },
    []
  );

  const selectedCategoryHasChildren =
    Number(selectedRootCategory?.children?.length) > 1;
  const selectedCollectionHasChildren =
    Number(selectedRootCollection?.children?.length) > 0;

  const onRootCategoryClick = () => {
    if (selectedCategoryHasChildren || selectedCollectionHasChildren) {
      setCategoryListOpened((prevState) => !prevState);
    } else {
      resetAllExceptStores();
    }
  };

  const selectCategory = (id: string) => {
    selectedCategoryId && applyCategories([id]);
    selectedCollectionId && applyCollections([id]);
    closeCategoryList();
  };

  return (
    <div className={styles.root}>
      <div className={styles.scroll}>
        <div className={styles.line}>
          {selectedCategory && (
            <AppliedFiltersCategory
              selectedCategory={selectedCategory}
              selectOpened={categoryListOpened}
              onClick={onRootCategoryClick}
              withArrow={selectedCategoryHasChildren}
            />
          )}

          {selectedCollection && (
            <AppliedFiltersCategory
              selectedCategory={selectedCollection}
              selectOpened={categoryListOpened}
              onClick={onRootCategoryClick}
              withArrow={selectedCollectionHasChildren}
            />
          )}

          {Boolean(appliedSearch) && (
            <AppliedFiltersItem label={appliedSearch} onRemove={resetSearch} />
          )}

          {appliedFilters.map((appliedFilter) => {
            const id = appliedFilter.id;
            if (!id) return null;
            const onClick = () => {
              selectAndApplySingleSecondFilter(id, appliedFilter.type);
            };
            return (
              <AppliedFiltersItem
                label={String(appliedFilter.label)}
                onRemove={onClick}
                key={id}
              />
            );
          })}
        </div>
      </div>

      {categoryListOpened &&
        selectedRootCategory &&
        selectedCategoryHasChildren && (
          <SecondaryCategorySelect
            category={selectedRootCategory}
            selectCategory={selectCategory}
            selectedCategoryId={String(selectedCategoryId)}
            categoryListRef={categoryListRef}
          />
        )}

      {categoryListOpened &&
        selectedRootCollection &&
        selectedCollectionHasChildren && (
          <SecondaryCategorySelect
            category={selectedRootCollection}
            selectCategory={selectCategory}
            selectedCategoryId={String(selectedCategoryId)}
            categoryListRef={categoryListRef}
          />
        )}
    </div>
  );
};

interface AppliedFilterInterface {
  id?: string | number;
  label?: string | number;
  type: keyof SecondFilterSelect;
}

const getFilterItem = (
  filters: FilterResponse,
  filterKey: keyof SecondFilterSelect,
  id: string | number
): AppliedFilterInterface => {
  if (filterKey === "discounts") {
    const discount = filters.discounts.find(
      (discount) => discount.value === id
    );
    return {
      id: discount?.value,
      label: discount && getDiscountDisplayName(discount.value),
      type: filterKey,
    };
  } else if (filterKey === "sizes") {
    const size = [...filters.sizes[0].sizes, ...filters.sizes[1].sizes].find(
      (size) => size.id === id
    );
    return {
      id: size?.id,
      label: size?.name,
      type: filterKey,
    };
  } else {
    const color = filters.colors.find((color) => color.id === id);
    return {
      id: color?.id,
      label: color?.name,
      type: filterKey,
    };
  }
};

export default AppliedFilters;
